import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Genre Jukebox product: the – / + quantity buttons, and Add to cart (after launch).
 *
 * Add to cart posts with fetch the same way Horizon's product form does: it dispatches a
 * CartLinesUpdateEvent (action "add") with the re-rendered cart sections, so the GJ cart drawer
 * refreshes, opens when "Auto open cart drawer" is on, and the header count updates.
 * Without a cart component on the page the form submits normally (to /cart).
 *
 * Thumbnails (two or more product images) swap the main picture in place.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [quantity] - The quantity input.
 * @property {HTMLElement} [error] - Add to cart error message.
 *
 * @extends {Component<Refs>}
 */
class GjProductComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#onSubmit);
  }

  /**
   * Shows the clicked thumbnail's image as the main picture.
   * @param {MouseEvent} event
   */
  showImage(event) {
    // Declarative events (on:click) hand over the element with the attribute as `target`
    const thumb = event.target instanceof Element ? event.target.closest('.gj-product__thumb') : null;
    // image_tag can't add a `ref`, so the main picture is found by its class
    const image = this.querySelector('img.gj-product__main-image');
    if (!thumb || !(image instanceof HTMLImageElement)) return;

    const { src, srcset, alt } = thumb.dataset;
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;
    image.alt = alt ?? '';

    for (const other of this.querySelectorAll('.gj-product__thumb')) {
      other.setAttribute('aria-pressed', String(other === thumb));
    }
  }

  decrease() {
    this.#step(-1);
  }

  increase() {
    this.#step(1);
  }

  /** @param {number} delta */
  #step(delta) {
    const input = this.refs.quantity;
    if (!input || input.disabled) return;

    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (Number(input.value) || min) + delta));
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('gj-product__buy')) return;

    const cartComponents = /** @type {HTMLElement[]} */ ([...document.querySelectorAll('cart-items-component')]);
    if (cartComponents.length === 0) return; // no drawer on this page: normal submit to /cart

    event.preventDefault();

    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[type="submit"]'));
    if (button?.disabled) return;
    if (button) button.disabled = true;
    this.#showError('');

    const formData = new FormData(form);
    const sectionIds = cartComponents.map((element) => element.dataset.sectionId).filter(Boolean);
    if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();
      const didError = Boolean(data.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'gj-product',
          itemCount: quantity,
          sections: didError ? undefined : data.sections,
          didError,
        },
      });

      if (didError) this.#showError(data.description || data.message || 'Could not add to cart.');
    } catch (error) {
      deferred.reject(error);
      this.#showError('Could not add to cart. Please try again.');
    } finally {
      if (button) button.disabled = false;
    }
  };

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message;
    error.hidden = message === '';
  }
}

if (!customElements.get('gj-product-component')) {
  customElements.define('gj-product-component', GjProductComponent);
}
